import React, { useState } from 'react';
import api from '../../services/api';
import authService from '../../services/authService';
import UploadModal from "./components/UploadModal";
import XmlSupplierScreen from "./XmlSupplierScreen";
import XmlBatchDashboard from "./XmlBatchDashboard";
import XmlScanHome from "./XmlScanHome";

export default function XmlScan() {
  const [scanning, setScanning] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [analysisBatches, setAnalysisBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [view, setView] = useState('list');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [progressLogs, setProgressLogs] = useState([]);
  const [currentProgress, setCurrentProgress] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { batchId, batchName }
  const [selectedSupplier, setSelectedSupplier] = useState(null); // supplier for modal

  // Carregar histórico ao montar componente
  React.useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await api.get('/xml-scan/history');
      if (response.data.success) {
        setAnalysisBatches(response.data.data);
        setUpdatedAt(Date.now());
      } else {
        setLoadFailed(true);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStartScan = () => {
    setShowUploadModal(true);
  };

  const handleUpload = async (file) => {
    console.log('Arquivo selecionado:', file);
    setScanning(true);
    setProgressLogs([]);
    setCurrentProgress(null);
    
    try {
      // Criar FormData para upload
      const formData = new FormData();
      formData.append('zipFile', file);
      
      // Usar XMLHttpRequest para evitar timeout do axios (igual upload screen)
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.vidal-app.com';
      const token = authService.getToken();
      const projeto = authService.getSelectedProject();
      
      const xhr = new XMLHttpRequest();
      
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            const loadedMB = (event.loaded / 1024 / 1024).toFixed(2);
            const totalMB = (event.total / 1024 / 1024).toFixed(2);
            
            // Atualizar componente visual de progresso (não logs)
            setCurrentProgress({
              stage: 'UPLOAD',
              stageName: 'Upload',
              percentual: percent,
              loadedMB: loadedMB,
              totalMB: totalMB,
            });
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.error || 'Erro no upload'));
            } catch {
              reject(new Error('Erro no upload'));
            }
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Erro de conexão')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelado')));

        xhr.open('POST', `${API_URL}/xml-scan/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('projeto', projeto);
        xhr.send(formData);
      });

      const response = await uploadPromise;
      
      if (response.success) {
        const operationId = response.operationId;
        
        // Conectar ao WebSocket para receber atualizações em tempo real
        const WS_URL = process.env.REACT_APP_WS_URL || 'wss://log.vidal-app.com/ws';
        const wsToken = authService.getToken();
        const wsUrlWithToken = `${WS_URL}?token=${wsToken}`;
        
        console.log('[XmlScan] Connecting to WebSocket with token:', wsToken ? '✅ Token exists' : '❌ No token');
        const ws = new WebSocket(wsUrlWithToken);
        
        ws.onopen = () => {
          console.log('[XmlScan] WebSocket conectado para operação:', operationId);
          
          // Inscrever-se para receber mensagens reforma_zip (igual ProcessingScreen)
          ws.send(JSON.stringify({
            action: 'subscribe',
            type: 'reforma_zip_progress',
            projectId: projeto
          }));
          ws.send(JSON.stringify({
            action: 'subscribe',
            type: 'reforma_zip_complete',
            projectId: projeto
          }));
          ws.send(JSON.stringify({
            action: 'subscribe',
            type: 'reforma_zip_error',
            projectId: projeto
          }));
          console.log('[XmlScan] Subscribed to reforma_zip messages for project:', projeto);
        };
        
        ws.onmessage = (event) => {
          try {
            const rawData = JSON.parse(event.data);
            
            // Ignorar mensagem de confirmação de inscrição
            if (rawData.success && rawData.message === 'Inscrição realizada com sucesso') return;
            
            // WebSocket server encapsula mensagens em {success: true, message: data}
            const data = rawData.message || rawData;
            
            console.log('[XmlScan] Received message:', data);
          
          if (data.operationId === operationId) {
            if (data.type === 'reforma_zip_progress') {
              // Atualizar progresso
              const logMessage = `${new Date().toLocaleTimeString()} - ${data.message || 'Processando...'}`;
              setProgressLogs(prev => [...prev, logMessage]);
              
              // Sempre atualizar currentProgress quando temos stage info
              console.log('[XmlScan] Updating progress:', {
                stage: data.stage,
                percentual: data.percentual,
                progress: data.progress
              });
              
              setCurrentProgress({
                progress: data.progress ? parseFloat(data.progress) : 0,
                stage: data.stage,
                stageName: data.stageName,
                etapa: data.etapa,
                processed: data.processed,
                total: data.total,
                conformes: data.conformes || 0,
                parciais: data.parciais || 0,
                naoConformes: data.naoConformes || 0,
                totalXmls: data.totalXmls,
                totalFornecedores: data.totalFornecedores,
                percentual: data.percentual,
                loadedMB: data.loadedMB,
                totalMB: data.totalMB,
              });
            } else if (data.type === 'reforma_zip_complete' && data.result) {
              // Análise concluída
              setProgressLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ✅ Análise concluída com sucesso!`]);
              setAnalysisBatches(prev => [data.result, ...prev]);
              setScanning(false);
              ws.close();
              
              // Recarregar histórico
              loadHistory();
            } else if (data.type === 'reforma_zip_error') {
              console.error('Erro na análise:', data.error);
              setProgressLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ❌ Erro: ${data.error}`]);
              setScanning(false);
              ws.close();
            }
          }
          } catch (e) {
            console.error('[XmlScan] Error parsing message:', e);
          }
        };
        
        ws.onerror = (error) => {
          console.error('Erro no WebSocket:', error);
          setProgressLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ❌ Erro de conexão WebSocket`]);
          setScanning(false);
        };
        
        ws.onclose = () => {
          console.log('WebSocket fechado');
        };
        
        // Timeout de 30 minutos para arquivos grandes
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.close();
            setScanning(false);
            setProgressLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ⏱️ Timeout - processamento muito longo`]);
          }
        }, 1800000);
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao enviar arquivo: ' + (error.response?.data?.error || error.message));
      setScanning(false);
    }
    
    return Promise.resolve();
  };

  const handleBatchClick = (batch) => {
    setSelectedBatch(batch);
    setView('detail');
  };

  const handleBackToList = () => {
    setSelectedBatch(null);
    setView('list');
  };

  const handleDeleteBatch = (batchId, batchName) => {
    setDeleteConfirm({ batchId, batchName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { batchId } = deleteConfirm;
    setDeleteConfirm(null);
    await deleteBatchById(batchId);
  };

  const deleteBatchById = async (batchId) => {
    try {
      await api.delete(`/xml-scan/${batchId}`);
      setAnalysisBatches(prev => prev.filter(batch => batch.operationId !== batchId));
      if (selectedBatch?.operationId === batchId) {
        handleBackToList();
      }
    } catch (error) {
      console.error('Erro ao excluir análise:', error);
    }
  };

  // Se estiver na view de detalhe do fornecedor
  if (view === 'supplier-detail' && selectedSupplier && selectedBatch) {
    return (
      <XmlSupplierScreen
        key={`${selectedSupplier.id}-${selectedSupplier.cnpj}`}
        supplier={selectedSupplier}
        batch={selectedBatch}
        batches={analysisBatches}
        onBack={() => {
          setSelectedSupplier(null);
          setView('detail');
        }}
        onOpenSupplier={(next) => setSelectedSupplier(next)}
      />
    );
  }

  // Tela de uma análise
  if (view === 'detail' && selectedBatch) {
    return (
      <XmlBatchDashboard
        key={selectedBatch.id}
        batch={selectedBatch}
        batches={analysisBatches}
        onBack={handleBackToList}
        onOpenSupplier={(supplier) => { setSelectedSupplier(supplier); setView('supplier-detail'); }}
        onDelete={deleteBatchById}
      />
    );
  }

  // Tela inicial: dashboard e diretório de análises
  return (
    <>
      <XmlScanHome
        batches={analysisBatches}
        loading={loading}
        failed={loadFailed}
        scanning={scanning}
        updatedAt={updatedAt}
        onRefresh={loadHistory}
        onCreate={handleStartScan}
        onOpenBatch={handleBatchClick}
        onOpenSupplier={(batch, supplier) => { setSelectedBatch(batch); setSelectedSupplier(supplier); setView('supplier-detail'); }}
        onAskDelete={handleDeleteBatch}
        deleteConfirm={deleteConfirm}
        onCancelDelete={() => setDeleteConfirm(null)}
        onConfirmDelete={confirmDelete}
      />

      {/* Upload Modal */}
      <UploadModal 
        isOpen={showUploadModal} 
        onClose={() => {
          setShowUploadModal(false);
          setProgressLogs([]);
          setCurrentProgress(null);
        }} 
        onUpload={handleUpload}
        progressLogs={progressLogs}
        currentProgress={currentProgress}
        isProcessing={scanning}
      />
    </>
  );
}
